import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { cart, loading, updateQuantity, removeFromCart } = useCart();

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      const success = await removeFromCart(productId);
      if (success) toast.success('Item removed from cart');
    } else {
      await updateQuantity(productId, newQuantity);
    }
  };

  const handleRemove = async (productId, productName) => {
    const success = await removeFromCart(productId);
    if (success) {
      toast.success(`${productName} removed from cart`);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] pt-24 pb-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-12">
          <div className="bg-white rounded-xl p-12 shadow-sm border border-[#E5E5E7]">
            <ShoppingBag className="w-12 h-12 text-[#8E8E93] mx-auto mb-6" />
            <h1 className="text-2xl font-extrabold text-[#1C1C1E] mb-3">
              Sign in to view your cart
            </h1>
            <p className="text-[#8E8E93] mb-8 text-sm">
              Create an account to start shopping
            </p>
            <Link to="/login">
              <Button 
                className="rounded-lg bg-[#0F0F0F] text-white font-semibold px-8 py-4 hover:bg-[#2C2C2E]"
                data-testid="cart-login-btn"
              >
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] pt-24 pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-28 bg-white rounded-xl skeleton"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] pt-24 pb-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-12">
          <div className="bg-white rounded-xl p-12 shadow-sm border border-[#E5E5E7]">
            <ShoppingBag className="w-12 h-12 text-[#8E8E93] mx-auto mb-6" />
            <h1 className="text-2xl font-extrabold text-[#1C1C1E] mb-3">
              Your cart is empty
            </h1>
            <p className="text-[#8E8E93] mb-8 text-sm">
              Browse our extensive catalog of three products
            </p>
            <Link to="/products">
              <Button 
                className="rounded-lg bg-[#FF4D00] text-white font-semibold px-8 py-4 hover:bg-[#CC3D00]"
                data-testid="cart-shop-btn"
              >
                Browse Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] pt-24 pb-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <h1 className="text-3xl font-extrabold text-[#1C1C1E] mb-8">
          Your Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div 
                key={item.product_id}
                className="bg-white rounded-xl p-5 shadow-sm border border-[#E5E5E7] flex gap-5"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Product Image */}
                <Link to={`/products/${item.product_id}`} className="shrink-0">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#F5F5F7]">
                    <img 
                      src={item.image_url} 
                      alt={item.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </div>
                </Link>

                {/* Product Info */}
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between items-start">
                    <div>
                      <Link 
                        to={`/products/${item.product_id}`}
                        className="font-bold text-[#1C1C1E] hover:text-[#FF4D00] transition-colors"
                      >
                        {item.name}
                      </Link>
                      <p className="text-[#8E8E93] text-sm">${item.price.toFixed(2)} each</p>
                    </div>
                    <button 
                      onClick={() => handleRemove(item.product_id, item.name)}
                      className="p-2 rounded-lg hover:bg-[#F5F5F7] text-[#8E8E93] hover:text-red-500 transition-colors"
                      data-testid={`remove-${item.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-auto flex items-center justify-between">
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-1 bg-[#F5F5F7] rounded-lg p-1">
                      <button 
                        onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                        className="w-8 h-8 rounded-md bg-white flex items-center justify-center hover:bg-[#E5E5E7] transition-colors"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center font-bold text-[#1C1C1E] text-sm">
                        {item.quantity}
                      </span>
                      <button 
                        onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                        className="w-8 h-8 rounded-md bg-white flex items-center justify-center hover:bg-[#E5E5E7] transition-colors"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>

                    {/* Item Total */}
                    <span className="font-bold text-[#1C1C1E]">
                      ${item.item_total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E7] sticky top-24">
              <h2 className="text-lg font-bold text-[#1C1C1E] mb-6">
                Order Summary
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#8E8E93] text-sm">
                  <span>Subtotal</span>
                  <span>${cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#8E8E93] text-sm">
                  <span>Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
              </div>

              <div className="border-t border-[#E5E5E7] pt-4 mb-6">
                <div className="flex justify-between text-lg font-bold text-[#1C1C1E]">
                  <span>Total</span>
                  <span>${cart.total.toFixed(2)}</span>
                </div>
              </div>

              <Button 
                onClick={() => navigate('/checkout')}
                className="w-full rounded-lg bg-[#FF4D00] text-white font-bold py-5 hover:bg-[#CC3D00] transition-colors"
                data-testid="checkout-btn"
              >
                Checkout
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              <Link to="/products" className="block mt-3">
                <Button 
                  variant="ghost"
                  className="w-full text-[#8E8E93] hover:text-[#1C1C1E] text-sm"
                >
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
